import { SITE } from '@/site';

const KEY = SITE.storageKeys.cookieConsent;
/** A saved choice is asked again after this long (mirrored in index.html). */
export const CONSENT_MAX_AGE_MS = 180 * 864e5;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    __jscLoadTracking?: () => void;
    /** Resolves true for the EU, EEA, UK, Switzerland and any visitor we cannot place. */
    __jscRegion?: Promise<boolean>;
    /** Set once the region lookup has answered. */
    __jscRestricted?: boolean;
  }
}

/** Fired by the footer Cookie settings link outside the restricted regions; CookieConsent opens the opt-out panel. */
export const OPEN_SETTINGS_EVENT = 'jsc:cookie-settings';

/** True when the visitor is in a consent-first region or the lookup failed (fail closed). */
export function isRestrictedRegion(): Promise<boolean> {
  return (window.__jscRegion ?? Promise.resolve(true)).catch(() => true);
}

/**
 * Footer "Cookie settings". Restricted regions go back to the opt-in banner;
 * everywhere else a small Accept / Decline panel opens, with no reload.
 */
export async function openCookieSettings() {
  if (await isRestrictedRegion()) withdrawConsent();
  else window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

/** The saved choice, or null when none is saved or it is older than 180 days. */
export function readConsent(): 'accepted' | 'declined' | null {
  const value = localStorage.getItem(KEY);
  if (value !== 'accepted' && value !== 'declined') return null;
  const at = Number(localStorage.getItem(`${KEY}-at`));
  if (at && Date.now() - at > CONSENT_MAX_AGE_MS) {
    localStorage.removeItem(KEY);
    localStorage.removeItem(`${KEY}-at`);
    return null;
  }
  return value;
}

export function saveConsent(choice: 'accepted' | 'declined') {
  localStorage.setItem(KEY, choice);
  localStorage.setItem(`${KEY}-at`, String(Date.now()));
}

/**
 * Restricted regions, from the footer "Cookie settings": forget the saved choice, tell Google consent is
 * denied again, drop the Google Analytics cookies, and reload so ad and
 * analytics scripts that were already running are gone. The banner then asks
 * again, with analytics held off until it is answered.
 */
export function withdrawConsent() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(`${KEY}-at`);
    // Outside Europe analytics is on by default; this keeps it off after the reload until the banner is answered.
    sessionStorage.setItem('__jsc_reask', '1');
  } catch {
    /* storage blocked: nothing saved to remove */
  }
  denyGoogleStorage();
  window.location.reload();
}

/** Tell Google every storage type is denied and drop the Google cookies this site can see. */
function denyGoogleStorage() {
  window.gtag?.('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (!/^(_ga|_gid|_gat|__gads|__gpi|__eoi)/.test(name)) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    });
  });
}

/**
 * Banner Decline: Google storage goes back to denied right away and the
 * analytics cookies already set (outside Europe they are on by default) are removed.
 */
export function declineAnalytics() {
  denyGoogleStorage();
}
